import "./tube-variants.css"

// Bottom-to-top order (column-reverse stacks the first child at the bottom).
// Hue comes from a per-color class: .tv-ball stays colorless, so each hue is
// a dedicated class in tube-variants.css.
const STACK = [
  "tv-ball--coral",
  "tv-ball--emerald",
  "tv-ball--azure",
  "tv-ball--amber",
]

type Theme = "dark" | "light"

// Each pill is previewed against both a light and a dark stage so the dish
// reads correctly on either background. A few surface colors are adjusted per
// theme (see .tv-stage--dark overrides) so they stay legible.
const THEMES: readonly Theme[] = ["light", "dark"]

interface TubeVariant {
  className: string
  id: string
  logic: string
  name: string
}

// Eight pill treatments. Each swaps only the body background / border /
// box-shadow of .tv-tube — the silhouette stays a symmetric pill.
const VARIANTS: readonly TubeVariant[] = [
  { className: "tv-tube--plate", id: "plate", logic: "light metal, moderate inset", name: "Plate" },
  { className: "tv-tube--deep", id: "deep", logic: "light metal, strong top inset", name: "Deep dish" },
  { className: "tv-tube--soft", id: "soft", logic: "light, low-contrast dish", name: "Soft" },
  { className: "tv-tube--dark", id: "dark", logic: "dark metal, near-black cavity", name: "Dark metal" },
  { className: "tv-tube--glass", id: "glass", logic: "translucent, lit from inside", name: "Glass" },
  { className: "tv-tube--gradient", id: "gradient", logic: "colored body, tinted inset", name: "Gradient" },
  { className: "tv-tube--rim", id: "rim", logic: "metal + raised mouth ring", name: "Rimmed plate" },
  { className: "tv-tube--neon", id: "neon", logic: "black dish, colored inner glow", name: "Neon" },
]

export function TubeVariants() {
  return (
    <section aria-label="Pill tube variants" className="tv">
      <h2 className="text-xl font-bold">
        Tube variants — {VARIANTS.length} pill treatments × {THEMES.length} backgrounds (deep-plate)
      </h2>
      <p className="">
        Each tube is a symmetric pill (top end mirrors the bottom) with layered
        outer + inset box-shadows that make the opening read as a hollow dish.
        Every treatment is shown on both a light and a dark stage.
      </p>
      <div className="tv-grid">
        {VARIANTS.flatMap(({ className, id, logic, name })=>
          THEMES.map(theme=> (
            <figure className="tv-card" key={`${id}-${theme}`}>
              <div
                className={`
                  tv-stage
                  tv-stage--${theme}
                `}
              >
                <div
                  aria-hidden="true"
                  className={`
                    tv-tube
                    ${className}
                  `}
                >
                  {STACK.map((hueClass, ballIndex)=> (
                    <span
                      className={`tv-ball ${hueClass}`}
                      key={ballIndex}
                    />
                  ))}
                </div>
              </div>
              <figcaption className="tv-caption">
                <strong>{name} · {theme}</strong>
                <code>{logic}</code>
              </figcaption>
            </figure>
          )),
        )}
      </div>
    </section>
  )
}